import {
  ClipboardList,
  Download,
  ExternalLink,
  History,
  Layers,
  PenLine,
  TriangleAlert,
  UserRound,
  Users,
  UserX,
  X,
} from 'lucide-react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import { DocumentCategory } from 'common-src/models/PatientDocument/constants';

import UploadDocumentPopup from 'src/popups/UploadDocumentPopup';

import { athenaUrl, formatClock12, formatDayKeyLabel } from '../../helpers';
import {
  fetchAppointmentIntakeReportBlob,
  fetchPatientMocaReportBlob,
  fetchPatientSummaryPdfBlob,
  fetchPatientVisitNotePdfBlob,
} from '../../services/careOrchestrationApi';
import { MocaPromptAlert, useMocaPrompt } from '../MocaPrompt';
import {
  BriefField,
  BriefItem,
  BriefRow,
  clinicVisitTimes,
  DisplayEndTime,
  DisplayTime,
  FactRows,
  ItemAbsent,
  ItemAction,
  ItemLink,
  PanelHeadline,
  RowNote,
  RowToggle,
  visitCode,
  visitLabelWithoutCode,
  VisitSection,
  VisitSections,
} from '../Visit';
import styles from './AppointmentBrief.module.scss';
import caregiverStyles from './CaregiverLine.module.scss';
import cognitionStyles from './CognitionSection.module.scss';
import {
  formatVisitDate,
  getApptDay,
  getCaregiverLine,
  getCptTitle,
  getSeenBefore,
  getShownLastVisit,
  getVisitDetails,
  intakeUnavailable,
  isLowMoca,
  zusPatientUrl,
} from './helpers';
import intakeStyles from './IntakeSection.module.scss';
import modalStyles from './PdfModal.module.scss';
import seenStyles from './SeenBeforeSection.module.scss';
import StepStrip from './StepStrip';

const CAREGIVER_TONE = {
  ok: caregiverStyles.toneOk,
  warn: caregiverStyles.toneWarn,
  none: caregiverStyles.toneNone,
};

export const CaregiverLine = ({ appt, large = false, flush = false }) => {
  const { text, tone } = getCaregiverLine(appt);
  return (
    <span
      className={`${caregiverStyles.root} ${CAREGIVER_TONE[tone]} ${
        large ? caregiverStyles.large : ''
      } ${flush ? caregiverStyles.flush : ''}`}
    >
      <Users size={large ? 14 : 13} className={caregiverStyles.icon} aria-hidden="true" />
      {text}
    </span>
  );
};

CaregiverLine.propTypes = {
  appt: PropTypes.shape({ caregivers: PropTypes.arrayOf(PropTypes.object) }),
  large: PropTypes.bool,
  flush: PropTypes.bool,
};

const PdfModal = ({ title, subtitle, url, fileName, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className={modalStyles.overlay}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={modalStyles.dialog} role="dialog" aria-modal="true" aria-label={title}>
        <div className={modalStyles.header}>
          <div className={modalStyles.heading}>
            <h3 className={modalStyles.title}>{title}</h3>
            {subtitle && <p className={modalStyles.subtitle}>{subtitle}</p>}
          </div>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={modalStyles.headerButton}
          >
            Open in new tab
            <ExternalLink size={14} className={modalStyles.headerIcon} aria-hidden="true" />
          </a>
          <a href={url} download={fileName} className={modalStyles.headerButton}>
            <Download size={14} className={modalStyles.headerIcon} aria-hidden="true" />
            Download
          </a>
          <button type="button" className={modalStyles.close} onClick={onClose} aria-label="Close">
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        <object data={url} type="application/pdf" className={modalStyles.viewer}>
          <p className={modalStyles.fallback}>
            This browser can&apos;t show PDFs inline. Use Open in new tab or Download.
          </p>
        </object>
      </div>
    </div>
  );
};

PdfModal.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  url: PropTypes.string.isRequired,
  fileName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

const getIntakeAlerts = (appt, intake, todayKey) => {
  if (!intake) return [];

  const { status } = intake;
  const pastVisit = getApptDay(appt) < todayKey;
  const alerts = [];

  if (!pastVisit && (status === 'not_received' || status === 'not_sent')) {
    alerts.push('Intake: Form not received');
  }
  if (status === 'assistance_requested') {
    alerts.push('Intake: Assistance requested');
  }
  if (status === 'completed' && intake.flagCount > 0) {
    alerts.push(
      `Intake: ${intake.flagCount} alert ${intake.flagCount === 1 ? 'flag' : 'flags'} to review`,
    );
  }
  return alerts;
};

export const NeedsAttentionSection = ({ appt, intake, todayKey }) => {
  const alerts = getIntakeAlerts(appt, intake, todayKey);
  if (!alerts.length) return null;

  return (
    <BriefRow label="Needs attention" tone="alert" meta={alerts.length}>
      {alerts.map((alert) => {
        const [topic, ...rest] = alert.split(': ');
        return (
          <BriefItem key={alert}>
            <BriefField label={rest.length ? topic : 'Attention'}>
              <span className={styles.finding}>{rest.length ? rest.join(': ') : alert}</span>
            </BriefField>
          </BriefItem>
        );
      })}
    </BriefRow>
  );
};

NeedsAttentionSection.propTypes = {
  appt: PropTypes.object.isRequired,
  intake: PropTypes.shape({
    status: PropTypes.string,
    flagCount: PropTypes.number,
  }),
  todayKey: PropTypes.string.isRequired,
};

const SEEN_ICON = { seen: History, new: UserRound, none: UserX };
const SEEN_CLASS = {
  seen: seenStyles.toneSeen,
  new: seenStyles.toneNew,
  none: seenStyles.toneNone,
};

const toDayKey = (value) => moment(value).format('YYYY-MM-DD');

export const SeenBeforeSection = ({ appt, note, todayKey, expanded, onToggle }) => {
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  useEffect(
    () => () => {
      if (report?.url) window.URL.revokeObjectURL(report.url);
    },
    [report],
  );

  const seen = getSeenBefore(appt, todayKey);
  const SeenIcon = SEEN_ICON[seen.tone];
  const hasSections = !!note && (!!note.assessment || note.plan.length > 0);
  const noteDay = note ? toDayKey(note.notedAt) : null;
  const shownVisit = getShownLastVisit(appt);
  const noteIsLastVisit =
    !!note &&
    !!shownVisit &&
    `${shownVisit.date}`.slice(0, 10) === noteDay &&
    shownVisit.visitType === note.visitType;
  const signedBy = note?.clinician ? `Signed by ${note.clinician}` : null;
  const noteMeta = !note
    ? null
    : noteIsLastVisit
      ? signedBy
      : ['Visit note', note.visitType, formatVisitDate(noteDay, todayKey), signedBy]
          .filter(Boolean)
          .join(' \u00b7 ');

  const openNote = async () => {
    setError('');
    setOpening(true);
    try {
      const blob = await fetchPatientVisitNotePdfBlob(appt.patientId, note.id);
      setReport({
        url: window.URL.createObjectURL(blob),
        subtitle: [
          appt.patient?.name,
          note.visitType,
          moment(note.notedAt).format('MMMM D, YYYY'),
          note.clinician && `Signed by ${note.clinician}`,
        ]
          .filter(Boolean)
          .join(' \u00b7 '),
        fileName: `visit-note-${toDayKey(note.notedAt)}.pdf`,
      });
    } catch (e) {
      setError('Could not open the note.');
    } finally {
      setOpening(false);
    }
  };

  return (
    <BriefRow label="Seen before">
      <BriefItem
        actions={
          (note || shownVisit) &&
          (note ? (
            <ItemAction
              label={opening ? 'Opening...' : 'Note'}
              describes="the last visit's signed note"
              disabled={opening}
              onClick={openNote}
            />
          ) : (
            <ItemAbsent>No signed note</ItemAbsent>
          ))
        }
      >
        <BriefField label="Seen">
          <span className={`${seenStyles.seen} ${SEEN_CLASS[seen.tone]}`}>
            <SeenIcon size={14} className={seenStyles.seenIcon} aria-hidden="true" />
            {seen.label}
          </span>
        </BriefField>
        {hasSections ? (
          <BriefField label="From the note">
            <div className={`${seenStyles.sections} ${expanded ? '' : seenStyles.clamped}`}>
              {note.assessment && (
                <p className={seenStyles.assessment}>
                  <span className={seenStyles.sectionLabel}>Assessment: </span>
                  {note.assessment}
                </p>
              )}
              {note.plan.length > 0 && (
                <ul className={seenStyles.plan}>
                  {note.plan.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
            </div>
            <RowToggle expanded={expanded} onToggle={onToggle}>
              {expanded ? 'Show less' : 'Show the full assessment and plan'}
            </RowToggle>
          </BriefField>
        ) : (
          noteMeta && <BriefField label="Summary">{noteMeta}</BriefField>
        )}
        {error && <p className={seenStyles.error}>{error}</p>}
      </BriefItem>
      {report && (
        <PdfModal
          title="Visit note"
          subtitle={report.subtitle}
          url={report.url}
          fileName={report.fileName}
          onClose={() => setReport(null)}
        />
      )}
    </BriefRow>
  );
};

SeenBeforeSection.propTypes = {
  appt: PropTypes.shape({
    patientId: PropTypes.number,
    patient: PropTypes.shape({ name: PropTypes.string }),
    lastVisit: PropTypes.shape({ date: PropTypes.string }),
    lastVisitWithProvider: PropTypes.shape({ date: PropTypes.string }),
  }).isRequired,
  note: PropTypes.shape({
    id: PropTypes.string,
    notedAt: PropTypes.string,
    visitType: PropTypes.string,
    clinician: PropTypes.string,
    assessment: PropTypes.string,
    plan: PropTypes.arrayOf(PropTypes.string),
  }),
  todayKey: PropTypes.string.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const EARLIER_SHOWN = 2;

export const EarlierVisitsSection = ({ appt, visits, todayKey, isLoading = false }) => {
  const [all, setAll] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  useEffect(
    () => () => {
      if (report?.url) window.URL.revokeObjectURL(report.url);
    },
    [report],
  );

  if (isLoading) return null;

  if (!visits.length) {
    return (
      <BriefRow label="Earlier visits">
        <RowNote>
          {getShownLastVisit(appt) ? 'No earlier visits' : 'First visit with Mindspan'}
        </RowNote>
      </BriefRow>
    );
  }

  const openNote = async (visit) => {
    setError('');
    setOpeningId(visit.noteId);
    try {
      const blob = await fetchPatientVisitNotePdfBlob(appt.patientId, visit.noteId);
      setReport({
        url: window.URL.createObjectURL(blob),
        subtitle: [
          appt.patient?.name,
          visit.visitType,
          moment(visit.date).format('MMMM D, YYYY'),
          visit.clinician && `Signed by ${visit.clinician}`,
        ]
          .filter(Boolean)
          .join(' \u00b7 '),
        fileName: `visit-note-${visit.date}.pdf`,
      });
    } catch (e) {
      setError('Could not open the note.');
    } finally {
      setOpeningId(null);
    }
  };

  const shown = all ? visits : visits.slice(0, EARLIER_SHOWN);

  return (
    <BriefRow label="Earlier visits">
      {shown.map((visit) => (
        <BriefItem
          key={`${visit.date}-${visit.visitType}-${visit.noteId}`}
          actions={
            visit.noteId ? (
              <ItemAction
                label={openingId === visit.noteId ? 'Opening...' : 'Note'}
                describes={`visit note, ${visit.visitType}`}
                disabled={openingId === visit.noteId}
                onClick={() => openNote(visit)}
              />
            ) : (
              <ItemAbsent>No signed note</ItemAbsent>
            )
          }
        >
          <p className={seenStyles.earlierLine}>
            <span className={seenStyles.earlierDate}>
              {formatVisitDate(`${visit.date}`.slice(0, 10), todayKey)}
            </span>
            {[visit.visitType, visit.clinician].filter(Boolean).map((part, index) => (
              <React.Fragment key={part}>
                <span aria-hidden="true">{' \u00b7 '}</span>
                <span
                  className={index === 0 ? seenStyles.earlierType : seenStyles.earlierClinician}
                >
                  {part}
                </span>
              </React.Fragment>
            ))}
          </p>
          <RowNote>{visit.summary || 'No signed note on file'}</RowNote>
        </BriefItem>
      ))}
      {visits.length > EARLIER_SHOWN && (
        <div>
          <RowToggle expanded={all} onToggle={() => setAll((prev) => !prev)}>
            {all ? 'Show fewer' : `Show all ${visits.length} earlier visits`}
          </RowToggle>
        </div>
      )}
      {error && <p className={seenStyles.error}>{error}</p>}
      {report && (
        <PdfModal
          title="Visit note"
          subtitle={report.subtitle}
          url={report.url}
          fileName={report.fileName}
          onClose={() => setReport(null)}
        />
      )}
    </BriefRow>
  );
};

EarlierVisitsSection.propTypes = {
  appt: PropTypes.shape({
    patientId: PropTypes.number,
    patient: PropTypes.shape({ name: PropTypes.string }),
  }).isRequired,
  visits: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      visitType: PropTypes.string,
      clinician: PropTypes.string,
      summary: PropTypes.string,
      noteId: PropTypes.string,
    }),
  ).isRequired,
  todayKey: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
};

const getTrend = (results, todayKey) => {
  if (results.length < 2) return null;
  const latest = results[0];
  const first = results[results.length - 1];
  const diff = Math.round(latest.score - first.score);
  const since = formatVisitDate(moment(first.resultAt).format('YYYY-MM-DD'), todayKey);
  if (diff === 0) return `No change since ${since}`;
  return `${diff < 0 ? 'Down' : 'Up'} ${Math.abs(diff)} since ${since}`;
};

const MOCA_SHOWN = 5;

export const CognitionSection = ({
  patientId,
  patientName,
  results,
  isLoading = false,
  todayKey,
}) => {
  const [all, setAll] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  useEffect(
    () => () => {
      if (report?.url) window.URL.revokeObjectURL(report.url);
    },
    [report],
  );

  const trend = useMemo(() => getTrend(results, todayKey), [results, todayKey]);

  const openReport = async (result) => {
    setError('');
    setOpeningId(result.id);
    try {
      const blob = await fetchPatientMocaReportBlob(patientId, result.id);
      const date = moment(result.resultAt).format('YYYY-MM-DD');
      setReport({
        url: window.URL.createObjectURL(blob),
        title: 'MoCA report',
        subtitle: [
          patientName,
          `MoCA ${Math.round(result.score)}/${result.maxScore}`,
          moment(result.resultAt).format('MMMM D, YYYY'),
        ]
          .filter(Boolean)
          .join(' \u00b7 '),
        fileName: `moca-report-${date}.pdf`,
      });
    } catch (e) {
      setError('Could not open the report.');
    } finally {
      setOpeningId(null);
    }
  };

  if (isLoading) return null;

  if (!results.length) return null;

  const shown = all ? results : results.slice(0, MOCA_SHOWN);

  return (
    <BriefRow label="Cognition">
      {shown.map((result, index) => {
        const describes = `MoCA on ${formatVisitDate(moment(result.resultAt).format('YYYY-MM-DD'), todayKey)}`;
        return (
          <BriefItem
            key={result.id}
            actions={
              <>
                {result.hasVideo ? (
                  <ItemLink label="Video" describes={describes} href={result.reviewUrl} />
                ) : (
                  <ItemAbsent>No video</ItemAbsent>
                )}
                <ItemAction
                  label={openingId === result.id ? 'Opening...' : 'Report'}
                  describes={describes}
                  disabled={openingId === result.id}
                  onClick={() => openReport(result)}
                />
              </>
            }
          >
            <div className={cognitionStyles.row}>
              <span className={cognitionStyles.test}>MoCA</span>
              <span className={cognitionStyles.date}>
                {formatVisitDate(moment(result.resultAt).format('YYYY-MM-DD'), todayKey)}
              </span>
              <span
                className={`${cognitionStyles.score} ${
                  isLowMoca(result) ? cognitionStyles.scoreLow : ''
                }`}
              >
                {Math.round(result.score)}/{result.maxScore}
                {isLowMoca(result) && (
                  <>
                    <TriangleAlert size={12} aria-hidden="true" />
                    <span className={cognitionStyles.srOnly}>below 20</span>
                  </>
                )}
              </span>
              <span className={cognitionStyles.finding}>{index === 0 ? trend : null}</span>
            </div>
          </BriefItem>
        );
      })}
      {error && <p className={cognitionStyles.error}>{error}</p>}
      {results.length > MOCA_SHOWN && (
        <div>
          <RowToggle expanded={all} onToggle={() => setAll((prev) => !prev)}>
            {all ? 'Show fewer' : `Show all ${results.length} MoCA results`}
          </RowToggle>
        </div>
      )}
      {report && (
        <PdfModal
          title={report.title}
          subtitle={report.subtitle}
          url={report.url}
          fileName={report.fileName}
          onClose={() => setReport(null)}
        />
      )}
    </BriefRow>
  );
};

CognitionSection.propTypes = {
  patientId: PropTypes.number.isRequired,
  patientName: PropTypes.string,
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  todayKey: PropTypes.string.isRequired,
};

const STATUS_LABEL = {
  completed: 'Complete',
  not_received: 'Not received',
  assistance_requested: 'Assistance requested',
  not_sent: 'Not sent',
};

const dayOf = (value) => moment(value).format('YYYY-MM-DD');

const ageOf = (value) => {
  const days = moment().diff(moment(value), 'days');
  if (days < 14) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
};

export const NotOnFileSection = ({ results, intake, isInitialVisit, isLoading = false }) => {
  if (isLoading || intake === undefined) return null;
  const missing = [
    !results.length && 'MoCA',
    isInitialVisit && (intake === null || intake.status === 'not_sent') && 'Intake form',
  ].filter(Boolean);
  if (!missing.length) return null;
  return (
    <BriefRow label="Not on file">
      <RowNote>{missing.join(' \u00b7 ')}</RowNote>
    </BriefRow>
  );
};

NotOnFileSection.propTypes = {
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
  intake: PropTypes.object,
  isInitialVisit: PropTypes.bool,
  isLoading: PropTypes.bool,
};

export const VisitDetailsSection = ({ appt }) => {
  const details = getVisitDetails(appt);
  if (!details.length) return null;
  return (
    <BriefRow label="Visit details">
      <RowNote>{details.join(' \u00b7 ')}</RowNote>
    </BriefRow>
  );
};

VisitDetailsSection.propTypes = {
  appt: PropTypes.shape({
    cptCode: PropTypes.string,
    confirmed: PropTypes.bool,
    primaryInsurance: PropTypes.string,
  }).isRequired,
};

export const useIntakeReport = ({ appt, intake }) => {
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  useEffect(
    () => () => {
      if (report?.url) window.URL.revokeObjectURL(report.url);
    },
    [report],
  );

  useEffect(() => {
    setReport(null);
    setError('');
  }, [appt?.id]);

  const open = async () => {
    if (opening || !appt) return;
    setError('');
    setOpening(true);
    try {
      const blob = await fetchAppointmentIntakeReportBlob(appt.id);
      setReport({
        url: window.URL.createObjectURL(blob),
        subtitle: [
          appt.patient?.name,
          intake?.completedAt ? moment(intake.completedAt).format('MMMM D, YYYY') : null,
        ]
          .filter(Boolean)
          .join(' · '),
        fileName: `intake-report-${appt.patientId}.pdf`,
      });
    } catch (e) {
      setError('Could not open the report.');
    } finally {
      setOpening(false);
    }
  };

  const overlays = report ? (
    <PdfModal
      title="Intake report"
      subtitle={report.subtitle}
      url={report.url}
      fileName={report.fileName}
      onClose={() => setReport(null)}
    />
  ) : null;

  return { open, opening, error, overlays };
};

const intakeReportShape = PropTypes.shape({
  open: PropTypes.func,
  opening: PropTypes.bool,
  error: PropTypes.string,
  overlays: PropTypes.node,
});

export const IntakeSection = ({ appt, intake: loadedIntake, intakeReport, todayKey }) => {
  if (loadedIntake === undefined) return null;
  if (loadedIntake === null || loadedIntake.status === 'not_sent') return null;
  const intake = loadedIntake;

  const pastVisit = getApptDay(appt) < todayKey;
  let tone = 'neutral';
  if (intake.status === 'assistance_requested') tone = 'warn';
  if ((intake.status === 'not_received' || intake.status === 'not_sent') && !pastVisit)
    tone = 'alert';

  const detail =
    intake.status === 'completed' && intake.completedAt
      ? [
          `returned ${formatVisitDate(dayOf(intake.completedAt), todayKey)}`,
          intake.fromEarlierVisit && ageOf(intake.completedAt),
          intake.fromEarlierVisit && 'may be out of date',
        ]
          .filter(Boolean)
          .join(' \u00b7 ')
      : intake.status === 'not_received' && intake.sentAt
        ? `sent ${formatVisitDate(dayOf(intake.sentAt), todayKey)}`
        : null;

  return (
    <BriefRow label="Intake">
      <BriefItem
        actions={
          intake.status === 'completed' && intake.hasReport ? (
            <ItemAction
              label={intakeReport.opening ? 'Opening...' : 'Report'}
              describes="intake report"
              disabled={intakeReport.opening}
              onClick={intakeReport.open}
            />
          ) : (
            intake.status !== 'not_sent' && (
              <ItemAbsent>
                {intake.status === 'completed' ? 'No report on file' : 'No report yet'}
              </ItemAbsent>
            )
          )
        }
      >
        <BriefField label="Status">
          <span className={`${intakeStyles.status} ${intakeStyles[tone]}`}>
            {STATUS_LABEL[intake.status]}
          </span>
          {detail && <span className={intakeStyles.detail}>{` \u00b7 ${detail}`}</span>}
        </BriefField>
      </BriefItem>
    </BriefRow>
  );
};

IntakeSection.propTypes = {
  appt: PropTypes.shape({
    id: PropTypes.string,
    patientId: PropTypes.number,
    patient: PropTypes.shape({ name: PropTypes.string }),
  }).isRequired,
  intake: PropTypes.shape({
    status: PropTypes.string,
    sentAt: PropTypes.string,
    completedAt: PropTypes.string,
    hasReport: PropTypes.bool,
  }),
  intakeReport: intakeReportShape.isRequired,
  todayKey: PropTypes.string.isRequired,
};

const formatSex = (sex) => `${sex ?? ''}`.trim().charAt(0).toUpperCase();

export const usePatientSummary = ({ appt, summary, onUploaded }) => {
  const [opening, setOpening] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  useEffect(
    () => () => {
      if (report?.url) window.URL.revokeObjectURL(report.url);
    },
    [report],
  );

  const open = async () => {
    setError('');
    setOpening(true);
    try {
      const blob = await fetchPatientSummaryPdfBlob(appt.patientId, summary.id);
      setReport({
        url: window.URL.createObjectURL(blob),
        subtitle: [
          appt.patient?.name,
          `Uploaded ${moment(summary.uploadedAt).format('MMMM D, YYYY')}`,
        ]
          .filter(Boolean)
          .join(' \u00b7 '),
        fileName: summary.fileName || 'patient-summary.pdf',
      });
    } catch (e) {
      setError('Could not open the summary.');
    } finally {
      setOpening(false);
    }
  };

  const overlays = (
    <>
      {uploading && appt && (
        <UploadDocumentPopup
          patientId={appt.patientId}
          defaultCategory={DocumentCategory.PatientSummary}
          onClose={(uploaded) => {
            setUploading(false);
            if (uploaded === true) onUploaded();
          }}
        />
      )}
      {report && (
        <PdfModal
          title="Patient summary"
          subtitle={report.subtitle}
          url={report.url}
          fileName={report.fileName}
          onClose={() => setReport(null)}
        />
      )}
    </>
  );

  return { summary, opening, error, open, startUpload: () => setUploading(true), overlays };
};

const patientSummaryActionsShape = PropTypes.shape({
  summary: PropTypes.shape({
    id: PropTypes.string,
    fileName: PropTypes.string,
    uploadedAt: PropTypes.string,
    uploadedBy: PropTypes.string,
  }),
  opening: PropTypes.bool,
  error: PropTypes.string,
  open: PropTypes.func,
  startUpload: PropTypes.func,
});

export const PatientSummarySection = ({ actions, todayKey }) => {
  const { summary } = actions;
  if (summary === undefined) return null;
  if (summary === null) {
    return (
      <BriefRow label="Patient summary">
        <BriefItem
          actions={
            <ItemAction label="Upload" describes="patient summary" onClick={actions.startUpload} />
          }
        >
          <RowNote>No patient summary on file</RowNote>
        </BriefItem>
      </BriefRow>
    );
  }

  const uploaded = [
    summary.uploadedBy ? `Uploaded by ${summary.uploadedBy}` : 'Uploaded',
    formatVisitDate(moment(summary.uploadedAt).format('YYYY-MM-DD'), todayKey),
  ].join(' \u00b7 ');

  return (
    <BriefRow label="Patient summary">
      <BriefItem
        actions={
          <>
            <ItemAction
              label={actions.opening ? 'Opening...' : 'Summary'}
              describes="patient summary"
              disabled={actions.opening}
              onClick={actions.open}
            />
            <ItemAction
              label="Upload new"
              describes="patient summary"
              hasPopup={false}
              onClick={actions.startUpload}
            />
          </>
        }
      >
        <BriefField label="Source">{uploaded}</BriefField>
        {actions.error && <p className={seenStyles.error}>{actions.error}</p>}
      </BriefItem>
    </BriefRow>
  );
};

PatientSummarySection.propTypes = {
  actions: patientSummaryActionsShape.isRequired,
  todayKey: PropTypes.string.isRequired,
};

const AppointmentBrief = ({
  appt,
  departmentLabelById = {},
  mocaResults = [],
  intake,
  intakeFailed = false,
  intakeReport,
  todayKey,
  children,
}) => {
  const patient = appt?.patient;
  const moca = useMocaPrompt(appt, 'Provider HUD');
  const lastMoca = mocaResults[0] ?? null;
  const mocaTrend = getTrend(mocaResults, todayKey);
  const lastMocaLine = lastMoca
    ? [
        `Last MoCA ${Math.round(lastMoca.score)}/${lastMoca.maxScore} on ${formatVisitDate(
          moment(lastMoca.resultAt).format('YYYY-MM-DD'),
          todayKey,
        )}`,
        mocaTrend && `${mocaTrend.charAt(0).toLowerCase()}${mocaTrend.slice(1)}`,
      ]
        .filter(Boolean)
        .join(' \u00b7 ')
    : null;
  const times = clinicVisitTimes(appt);
  const code = visitCode({ cptCode: appt?.cptCode, visitType: appt?.visitType });
  const mocaValue = lastMocaLine ? lastMocaLine.replace(/^Last MoCA /, '') : 'None on file';
  const demographics = [patient?.age, formatSex(patient?.sex)].filter(Boolean).join(' ');
  const clinic = departmentLabelById[`${appt?.departmentAthenaId ?? ''}`] || '';
  const zusHref = zusPatientUrl(patient?.zusIntegration?.zusUpid);
  const chartHref = athenaUrl(
    patient?.athenaIntegration?.athenaPatientId,
    patient?.athenaIntegration?.practiceId,
  );
  const stages = [
    {
      stage: 'Prepare',
      steps: [
        {
          key: 'intake',
          icon: ClipboardList,
          label: intakeReport.opening ? 'Opening...' : 'Intake',
          caption: 'Their intake report, from before the visit',
          hasPopup: true,
          onClick: intakeReport.open,
          unavailable: intakeUnavailable(intake, intakeFailed),
        },
        {
          key: 'zus',
          icon: Layers,
          label: 'Zus',
          caption: 'Results and records from other doctors',
          href: zusHref ?? undefined,
          unavailable: zusHref ? undefined : 'No Zus record on file',
        },
      ],
    },
    {
      stage: 'Conduct',
      steps: [
        {
          key: 'athena',
          icon: PenLine,
          label: 'Athena',
          caption: 'The note, the orders and the signature, last',
          href: chartHref ?? undefined,
          unavailable: chartHref ? undefined : 'No Athena chart on file',
        },
      ],
    },
  ];

  return (
    <section className={styles.root}>
      <header>
        <PanelHeadline
          size="panel"
          divider
          visit={{
            code,
            kindName: appt?.visitType,
            label: code ? visitLabelWithoutCode(appt?.visitType) : appt?.visitType,
            hint: getCptTitle(appt),
          }}
          title={
            <>
              {patient?.name}
              {demographics && <span className={styles.demographics}>{demographics}</span>}
            </>
          }
        />
        <div className={styles.strip}>
          <StepStrip stages={stages} />
          {intakeReport.error && (
            <p role="alert" className={styles.actionError}>
              {intakeReport.error}
            </p>
          )}
        </div>
        <VisitSections layout="brief">
          <VisitSection label="When">
            <DisplayTime time={formatClock12(times.start)} zone={times.zone} />
            <DisplayEndTime>
              to {formatClock12(times.end)} {times.zone} · {times.minutes} min
            </DisplayEndTime>
            <span className={styles.whenDetail}>
              {[formatDayKeyLabel(getApptDay(appt)), clinic].filter(Boolean).join(' · ')}
            </span>
          </VisitSection>
          <VisitSection label="Visit span">
            <span className={styles.notSplit}>Not split in the huddle</span>
          </VisitSection>
          <VisitSection label="Member">
            <FactRows
              rows={[
                { label: 'MoCA', value: mocaValue },
                { label: 'Caregiver', value: <CaregiverLine appt={appt} large flush /> },
              ]}
            />
          </VisitSection>
        </VisitSections>

        {appt?.lowMocaPrompt && (
          <div className={styles.mocaPrompt}>
            <MocaPromptAlert
              latestMoca={appt.latestMoca}
              reviewUrl={lastMoca?.hasVideo ? lastMoca.reviewUrl : undefined}
              moca={moca}
              past={getApptDay(appt) < todayKey}
            />
          </div>
        )}
      </header>
      <div className={styles.rows}>{children}</div>
    </section>
  );
};

AppointmentBrief.propTypes = {
  appt: PropTypes.shape({
    visitType: PropTypes.string,
    cptCode: PropTypes.string,
    durationMinutes: PropTypes.number,
    departmentAthenaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lowMocaPrompt: PropTypes.bool,
    latestMoca: PropTypes.shape({
      score: PropTypes.number,
      maxScore: PropTypes.number,
      takenOn: PropTypes.string,
    }),
    patient: PropTypes.shape({
      name: PropTypes.string,
      age: PropTypes.number,
      sex: PropTypes.string,
      athenaIntegration: PropTypes.object,
      zusIntegration: PropTypes.shape({ zusUpid: PropTypes.string }),
    }),
  }).isRequired,
  departmentLabelById: PropTypes.object,
  mocaResults: PropTypes.arrayOf(
    PropTypes.shape({
      score: PropTypes.number,
      maxScore: PropTypes.number,
      resultAt: PropTypes.string,
    }),
  ),
  intake: PropTypes.shape({ status: PropTypes.string }),
  intakeFailed: PropTypes.bool,
  intakeReport: intakeReportShape.isRequired,
  todayKey: PropTypes.string,
  children: PropTypes.node,
};

export default AppointmentBrief;
